import React, { useEffect } from "react";
import "./legalPages.css";

const TermsAndConditions = () => {
  useEffect(() => {
    document.title = "Terms and Conditions - FNB Kitchen Mobile App";
    return () => {
      document.title = "FBK ADMIN";
    };
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Terms and Conditions</h1>
        <p className="last-updated">Last Updated: February 10, 2026</p>

        <div className="section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            Welcome to FNB Kitchen Mobile App. These Terms and Conditions (&quot;Terms&quot;) govern your access to and use of the FNB Kitchen Mobile App mobile application and related services (collectively, the &quot;Service&quot;). By downloading, installing, accessing, or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
          </p>
        </div>

        <div className="section">
          <h2>2. User Accounts and Registration</h2>

          <h3>2.1 Account Creation</h3>
          <p>To use the Service, you must create an account by providing accurate, complete, and current information including your name, email address, phone number, and password. You are responsible for maintaining the confidentiality of your account credentials.</p>

          <h3>2.2 Account Security</h3>
          <p>You are solely responsible for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account or any other security breach. We will not be liable for any loss or damage arising from your failure to protect your account credentials.</p>

          <h3>2.3 Account Accuracy</h3>
          <p>You agree to provide accurate and truthful information and to keep your account information up to date. Providing false or misleading information may result in immediate termination of your account.</p>
        </div>

        <div className="section">
          <h2>3. User Eligibility</h2>
          <p>You must be at least 18 years of age to use the Service. By using the Service, you represent and warrant that you are at least 18 years old and have the legal capacity to enter into these Terms. The Service is not intended for use by individuals under 18.</p>
        </div>

        <div className="section">
          <h2>4. License and Restrictions</h2>

          <h3>4.1 Limited License</h3>
          <p>Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your personal or business use in connection with field service management activities.</p>

          <h3>4.2 Restrictions</h3>
          <p>You agree NOT to:</p>
          <ul>
            <li>Modify, adapt, translate, or create derivative works of the Service</li>
            <li>Reverse engineer, decompile, disassemble, or attempt to discover the source code</li>
            <li>Remove, alter, or obscure any proprietary notices</li>
            <li>Use the Service for any unlawful purpose or in violation of these Terms</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Use automated systems (bots, scrapers) to access the Service</li>
            <li>Attempt to gain unauthorized access to any portion of the Service</li>
          </ul>
        </div>

        <div className="section">
          <h2>5. User Content and Responsibilities</h2>

          <h3>5.1 User-Generated Content</h3>
          <p>The Service allows you to upload, submit, or transmit content including service reports, photos, videos, digital signatures, and other materials (&quot;User Content&quot;). You retain ownership of your User Content, but grant us a worldwide, non-exclusive, royalty-free license to use, store, display, and transmit your User Content as necessary to provide the Service.</p>

          <h3>5.2 Content Standards</h3>
          <p>You are solely responsible for your User Content. You represent and warrant that your User Content:</p>
          <ul>
            <li>Does not violate any third-party rights (copyright, trademark, privacy, publicity)</li>
            <li>Does not contain unlawful, defamatory, obscene, or offensive material</li>
            <li>Does not contain viruses, malware, or harmful code</li>
            <li>Complies with all applicable laws and regulations</li>
          </ul>

          <h3>5.3 Content Removal</h3>
          <p>We reserve the right to review, monitor, and remove any User Content that violates these Terms or is otherwise objectionable, at our sole discretion, without prior notice.</p>
        </div>

        <div className="section">
          <h2>6. Service Reports and Documentation</h2>
          <p>The Service enables you to create and submit service reports containing photos, videos, signatures, and other documentation. You acknowledge that:</p>
          <ul>
            <li>Service reports may be shared with customers, supervisors, and administrators</li>
            <li>Digital signatures are legally binding and represent your agreement to the service report</li>
            <li>You are responsible for the accuracy and completeness of all service reports</li>
            <li>Service reports may be retained for up to 7 years for business and legal compliance purposes</li>
          </ul>
        </div>

        <div className="section">
          <h2>7. Payment and Billing</h2>
          <p>If the Service includes paid features or subscriptions:</p>
          <ul>
            <li>You agree to pay all applicable fees as described in the Service</li>
            <li>All fees are non-refundable unless otherwise stated</li>
            <li>We reserve the right to change fees with 30 days&apos; notice</li>
            <li>Failure to pay fees may result in suspension or termination of your account</li>
            <li>You are responsible for all taxes associated with your use of the Service</li>
          </ul>
        </div>

        <div className="section">
          <h2>8. Intellectual Property</h2>
          <p>The Service, including all content, features, functionality, software, and design, is owned by FNB Kitchen Mobile App and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. Our trademarks and trade dress may not be used without our prior written permission.</p>
        </div>

        <div className="section">
          <h2>9. Prohibited Activities</h2>
          <p>You agree not to engage in any of the following prohibited activities:</p>
          <ul>
            <li>Violating laws, regulations, or third-party rights</li>
            <li>Impersonating any person or entity</li>
            <li>Transmitting spam, chain letters, or unsolicited communications</li>
            <li>Uploading viruses, malware, or harmful code</li>
            <li>Attempting to gain unauthorized access to accounts or systems</li>
            <li>Collecting user information without consent</li>
            <li>Using the Service for fraudulent or illegal purposes</li>
            <li>Harassing, threatening, or abusing other users</li>
          </ul>
        </div>

        <div className="section">
          <h2>10. Termination</h2>

          <h3>10.1 Termination by You</h3>
          <p>You may terminate your account at any time by contacting us at <a href="mailto:info@fbk.com.sg">info@fbk.com.sg</a>. Upon termination, your personal data will be deleted within 30 days, except where retention is required by law.</p>

          <h3>10.2 Termination by Us</h3>
          <p>We reserve the right to suspend or terminate your account immediately, without prior notice, if you violate these Terms or engage in conduct that we deem inappropriate or harmful to the Service or other users.</p>

          <h3>10.3 Effect of Termination</h3>
          <p>Upon termination, your right to use the Service will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.</p>
        </div>

        <div className="section">
          <h2>11. Disclaimers and Warranties</h2>
          <div className="disclaimer">
            <p>THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.</p>
          </div>
          <p>We do not warrant that:</p>
          <ul>
            <li>The Service will be uninterrupted, secure, or error-free</li>
            <li>Defects will be corrected</li>
            <li>The Service is free of viruses or harmful components</li>
            <li>Results from using the Service will be accurate or reliable</li>
          </ul>
        </div>

        <div className="section">
          <h2>12. Limitation of Liability</h2>
          <div className="disclaimer">
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, FNB KITCHEN MOBILE APP SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE.</p>
          </div>
          <p>Our total liability for any claims arising out of or relating to these Terms or the Service shall not exceed the amount you paid us in the 12 months preceding the claim, or $100, whichever is greater.</p>
        </div>

        <div className="section">
          <h2>13. Indemnification</h2>
          <p>You agree to indemnify, defend, and hold harmless FNB Kitchen Mobile App and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including legal fees) arising out of or related to:</p>
          <ul>
            <li>Your use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights</li>
            <li>Your User Content</li>
          </ul>
        </div>

        <div className="section">
          <h2>14. Modifications to Terms</h2>
          <p>We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms in the app and updating the &quot;Last Updated&quot; date. Your continued use of the Service after changes become effective constitutes acceptance of the modified Terms.</p>
        </div>

        <div className="section">
          <h2>15. Governing Law</h2>
          <p>These Terms shall be governed by and construed in accordance with the laws of Singapore, without regard to its conflict of law principles. Any legal action or proceeding arising out of or related to these Terms shall be instituted exclusively in the courts of Singapore.</p>
        </div>

        <div className="section">
          <h2>16. Dispute Resolution</h2>
          <p>If you have any dispute arising out of these Terms or the Service, you agree to first contact us at <a href="mailto:info@fbk.com.sg">info@fbk.com.sg</a> to attempt to resolve the dispute informally. If the dispute cannot be resolved within 30 days, either party may pursue formal legal remedies.</p>
        </div>

        <div className="section">
          <h2>17. Severability</h2>
          <p>If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall remain in full force and effect. The invalid or unenforceable provision shall be replaced with a valid provision that most closely matches the intent of the original provision.</p>
        </div>

        <div className="section">
          <h2>18. Entire Agreement</h2>
          <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and FNB Kitchen Mobile App regarding the Service and supersede all prior agreements, understandings, and communications, whether written or oral.</p>
        </div>

        <div className="section">
          <h2>19. Contact Information</h2>
          <p>If you have questions about these Terms and Conditions, contact us:</p>
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:info@fbk.com.sg">info@fbk.com.sg</a></p>
            <p><strong>Phone:</strong> <a href="tel:+6591258874">+65 9125 8874</a></p>
            <p><strong>Address:</strong> 3 Ubi Ave 3, Crocodile house #03-04 Singapore 408857</p>
          </div>
        </div>

        <div className="section">
          <h2>20. Acknowledgment</h2>
          <div className="acknowledgment-box">
            <p><strong>By using FNB Kitchen Mobile App, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree with these Terms, you must discontinue use of the Service immediately.</strong></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
